export enum SupplierTypeGroup {
    TECHNOLOGY = "technology",
    PROFESSIONAL_SERVICES = "professional_services",
    OPERATIONS_SUPPLY_CHAIN = "operations_supply_chain",
    PEOPLE_BENEFITS = "people_benefits",
    INDUSTRY_SPECIFIC = "industry_specific",
    OTHER = "other",
}

export enum SupplierType {
    SOFTWARE_SAAS = "software_saas",
    CLOUD_INFRASTRUCTURE = "cloud_infrastructure",
    TELECOM_CONNECTIVITY = "telecom_connectivity",
    CYBERSECURITY = "cybersecurity",
    CONSULTING_ADVISORY = "consulting_advisory",
    LEGAL_SERVICES = "legal_services",
    STAFFING_RECRUITMENT = "staffing_recruitment",
    MARKETING_ADVERTISING = "marketing_advertising",
    FINANCIAL_SERVICES = "financial_services",
    MANUFACTURING_INDUSTRIAL = "manufacturing_industrial",
    LOGISTICS_DISTRIBUTION = "logistics_distribution",
    RAW_MATERIALS_COMMODITIES = "raw_materials_commodities",
    FACILITIES_REAL_ESTATE = "facilities_real_estate",
    CONSTRUCTION_ENGINEERING = "construction_engineering",
    FLEET_TRANSPORTATION = "fleet_transportation",
    BENEFITS_INSURANCE = "benefits_insurance",
    TRAINING_DEVELOPMENT = "training_development",
    PAYROLL_HR_TECH = "payroll_hr_tech",
    HEALTHCARE_LIFE_SCIENCES = "healthcare_life_sciences",
    ENERGY_UTILITIES = "energy_utilities",
    FOOD_BEVERAGE = "food_beverage",
    OTHER_CUSTOM = "other_custom",
}

export const SUPPLIER_TYPE_GROUPS: Record<SupplierTypeGroup, SupplierType[]> = {
    [SupplierTypeGroup.TECHNOLOGY]: [
        SupplierType.SOFTWARE_SAAS,
        SupplierType.CLOUD_INFRASTRUCTURE,
        SupplierType.TELECOM_CONNECTIVITY,
        SupplierType.CYBERSECURITY,
    ],
    [SupplierTypeGroup.PROFESSIONAL_SERVICES]: [
        SupplierType.CONSULTING_ADVISORY,
        SupplierType.LEGAL_SERVICES,
        SupplierType.STAFFING_RECRUITMENT,
        SupplierType.MARKETING_ADVERTISING,
        SupplierType.FINANCIAL_SERVICES,
    ],
    [SupplierTypeGroup.OPERATIONS_SUPPLY_CHAIN]: [
        SupplierType.MANUFACTURING_INDUSTRIAL,
        SupplierType.LOGISTICS_DISTRIBUTION,
        SupplierType.RAW_MATERIALS_COMMODITIES,
        SupplierType.FACILITIES_REAL_ESTATE,
        SupplierType.CONSTRUCTION_ENGINEERING,
        SupplierType.FLEET_TRANSPORTATION,
    ],
    [SupplierTypeGroup.PEOPLE_BENEFITS]: [
        SupplierType.BENEFITS_INSURANCE,
        SupplierType.TRAINING_DEVELOPMENT,
        SupplierType.PAYROLL_HR_TECH,
    ],
    [SupplierTypeGroup.INDUSTRY_SPECIFIC]: [
        SupplierType.HEALTHCARE_LIFE_SCIENCES,
        SupplierType.ENERGY_UTILITIES,
        SupplierType.FOOD_BEVERAGE,
    ],
    [SupplierTypeGroup.OTHER]: [SupplierType.OTHER_CUSTOM],
};

export const SUPPLIER_TYPE_GROUP_LABELS: Record<SupplierTypeGroup, string> = {
    [SupplierTypeGroup.TECHNOLOGY]: "Technology",
    [SupplierTypeGroup.PROFESSIONAL_SERVICES]: "Professional Services",
    [SupplierTypeGroup.OPERATIONS_SUPPLY_CHAIN]: "Operations & Supply Chain",
    [SupplierTypeGroup.PEOPLE_BENEFITS]: "People & Benefits",
    [SupplierTypeGroup.INDUSTRY_SPECIFIC]: "Industry Specific",
    [SupplierTypeGroup.OTHER]: "Other",
};

export const SUPPLIER_TYPE_LABELS: Record<SupplierType, string> = {
    [SupplierType.SOFTWARE_SAAS]: "Software / SaaS",
    [SupplierType.CLOUD_INFRASTRUCTURE]: "Cloud & Infrastructure",
    [SupplierType.TELECOM_CONNECTIVITY]: "Telecom & Connectivity",
    [SupplierType.CYBERSECURITY]: "Cybersecurity",
    [SupplierType.CONSULTING_ADVISORY]: "Consulting & Advisory",
    [SupplierType.LEGAL_SERVICES]: "Legal Services",
    [SupplierType.STAFFING_RECRUITMENT]: "Staffing & Recruitment",
    [SupplierType.MARKETING_ADVERTISING]: "Marketing & Advertising",
    [SupplierType.FINANCIAL_SERVICES]: "Financial Services",
    [SupplierType.MANUFACTURING_INDUSTRIAL]: "Manufacturing & Industrial",
    [SupplierType.LOGISTICS_DISTRIBUTION]: "Logistics & Distribution",
    [SupplierType.RAW_MATERIALS_COMMODITIES]: "Raw Materials & Commodities",
    [SupplierType.FACILITIES_REAL_ESTATE]: "Facilities & Real Estate",
    [SupplierType.CONSTRUCTION_ENGINEERING]: "Construction & Engineering",
    [SupplierType.FLEET_TRANSPORTATION]: "Fleet & Transportation",
    [SupplierType.BENEFITS_INSURANCE]: "Benefits & Insurance",
    [SupplierType.TRAINING_DEVELOPMENT]: "Training & Development",
    [SupplierType.PAYROLL_HR_TECH]: "Payroll & HR Tech",
    [SupplierType.HEALTHCARE_LIFE_SCIENCES]: "Healthcare & Life Sciences",
    [SupplierType.ENERGY_UTILITIES]: "Energy & Utilities",
    [SupplierType.FOOD_BEVERAGE]: "Food & Beverage",
    [SupplierType.OTHER_CUSTOM]: "Other / Custom",
};

export enum EvaluationGoal {
    NEW_SUPPLIER_EVALUATION = "new_supplier_evaluation",
    RENEWAL_DECISION = "renewal_decision",
    CONTRACT_NEGOTIATION_PREP = "contract_negotiation_prep",
    RISK_ASSESSMENT = "risk_assessment",
    COMPETITIVE_COMPARISON = "competitive_comparison",
    DUE_DILIGENCE = "due_diligence",
}

export enum BriefStatus {
    /** Submitted and waiting for a worker. Briefs are enqueued at creation, so
     *  there is no unsubmitted state; this was previously called DRAFT. */
    QUEUED = "queued",
    PROCESSING = "processing",
    COMPLETED = "completed",
    FAILED = "failed",
}

export enum BriefJobStatus {
    QUEUED = "queued",
    RESEARCHING = "researching",
    VERIFYING = "verifying",
    CORRECTING = "correcting",
    RENDERING = "rendering",
    COMPLETED = "completed",
    FAILED = "failed",
}

export enum Verdict {
    STRONG_YES = "STRONG_YES",
    YES = "YES",
    CONDITIONAL = "CONDITIONAL",
    RENEGOTIATE = "RENEGOTIATE",
    INSUFFICIENT_DATA = "INSUFFICIENT_DATA",
    NO = "NO",
    STRONG_NO = "STRONG_NO",
}

export interface BuyerBriefIntake {
    supplierName: string;
    supplierWebsite?: string;
    /** Set only when the analyst picked a real vendor from the typeahead. */
    vendorKey?: string;
    supplierType: SupplierType;
    supplierTypeGroup: SupplierTypeGroup;
    evaluationGoal: EvaluationGoal;
    selectedTopics: string[];
}

// Mirrors the server-side NewsItem/LatestNews types (node-services-v2
// BuyerBriefTypes.ts). The brief view renders briefHtml, so these are for
// parity/tooling only — briefOutput.latest_news carries this shape when the
// news step ran.
export interface NewsItem {
    title: string;
    url: string;
    source_name: string;
    published_date: string;
    summary: string;
    category?:
        | "financial"
        | "leadership"
        | "product"
        | "legal_regulatory"
        | "ma_ownership"
        | "security_incident"
        | "partnership"
        | "other";
}

export interface LatestNews {
    items: NewsItem[];
    searched_window_days: number;
    retrieved_at: string;
}

// Mirrors the server-side StockSnapshot types (node-services-v2
// BuyerBriefTypes.ts). Parity/tooling only — briefOutput.stock_snapshot
// carries this shape when the supplier (or its public parent) is publicly
// traded and the server's FMP lookup succeeded.
export type StockWindowKey = "1D" | "7D" | "30D" | "90D" | "6M" | "1Y";

export interface StockMovement {
    window: StockWindowKey;
    percent: number | null;
}

export interface StockSnapshot {
    ticker: string;
    exchange: string;
    listed_entity_name: string;
    relationship: "direct" | "parent";
    currency: string;
    price: number;
    movements: StockMovement[];
    retrieved_at: string;
}

// Mirrors the server-side FinancialTrend types (node-services-v2
// BuyerBriefTypes.ts). Parity/tooling only — briefOutput.financial_trend
// carries this shape when the FMP income-statement fetch succeeded for a
// validated ticker.
export interface FinancialTrendPeriod {
    label: string;
    revenue: number;
    cost_of_revenue: number;
    gross_profit: number;
}

export interface FinancialTrend {
    ticker: string;
    currency: string;
    annual: FinancialTrendPeriod[];
    quarterly: FinancialTrendPeriod[];
    retrieved_at: string;
}

// Mirrors the server-side WorkforceSnapshot type (node-services-v2
// BuyerBriefTypes.ts). Parity/tooling only — briefOutput.workforce_snapshot
// carries the FMP headcount for the always-on Workforce glance card.
export interface WorkforceSnapshot {
    current: number;
    previous?: number;
    delta_percent?: number;
    period: string;
    retrieved_at: string;
}

// Mirrors the server-side MarketSnapshot type (node-services-v2
// BuyerBriefTypes.ts). Parity/tooling only — briefOutput.market_snapshot is
// inference-pass output rendered as the Market at a Glance strip.
export interface MarketSnapshot {
    industry_direction?: "growing" | "stable" | "declining";
    industry_note?: string;
    market_leverage?: "buyer" | "balanced" | "supplier";
    leverage_note?: string;
    macro_risk?: "low" | "medium" | "high";
    risk_note?: string;
    claim_refs?: string[];
}

export interface BuyerBrief {
    id: number;
    organizationId: number;
    creatorId: number;
    supplierId?: number;
    vendorKey?: string;
    status: BriefStatus;
    supplierName: string;
    supplierWebsite?: string;
    supplierType: SupplierType;
    evaluationGoal: EvaluationGoal;
    selectedTopics: string[];
    intakePayload: BuyerBriefIntake;
    briefOutput?: any;
    briefHtml?: string;
    logoUrl?: string;
    /* No verificationResult. It is internal QA output — a PASS/FAIL grade plus
     * per-issue severities and types like "hallucination" and "staleness",
     * written about a named supplier. The server no longer sends it, and it is
     * not something to put in front of a buyer. */
    verdict?: Verdict;
    /** The model's own words for the verdict, shown in the index pill. Absent on
     *  briefs generated before this shipped; fall back to VERDICT_LABELS. */
    recommendationHeadline?: string;
    modelUsed?: string;
    webSearchesExecuted: number;
    errorDetails?: any;
    /** True when an Infinity Loop employee created this on the customer's
     *  behalf. Still sent by the server; nothing in the UI reads it since the
     *  index stopped splitting on who made the brief. */
    employeeInitiated?: boolean;
    isStuck?: boolean;
    /** Live pipeline stage. Present only while genuinely processing. */
    currentStep?: string;
    recordCreationDate: string;
    recordCompletionDate?: string;
    recordDeletionDate?: string;
}

/**
 * A legacy (pre-V2) brief, backed by il_supplier and viewed through the old
 * /supplier renderer. Legacy briefs have no evaluation goal or recommendation,
 * so the index shows the supplier, a description, and the creation date.
 */
export interface LegacyBriefListItem {
    source: "legacy";
    id: number;
    supplierName: string;
    logoUrl?: string;
    description?: string;
    recordCreationDate: string;
}

export type BriefListItem = (BuyerBrief & { source: "v2" }) | LegacyBriefListItem;

/**
 * Mirrors BRIEF_VIEWS on the server. Not tabs: `all`, `mine` and `researching`
 * are three slices of the same V2 list, split on whether the brief is linked to
 * a vendor the customer picked at intake. Only `earlier` is a separate source.
 * Still sent as `?tab=` on the wire, which the server has not renamed.
 */
export const BRIEF_VIEWS = ["all", "mine", "researching", "earlier"] as const;
export type BriefView = (typeof BRIEF_VIEWS)[number];

/**
 * Total briefs behind each view, unfiltered by any search term. `all` is the sum
 * of `mine` and `researching`. Used to decide how much of the view control is
 * worth drawing, not to label it: a filter whose two sides are not both
 * populated is not drawn at all.
 */
export type BriefViewCounts = Record<BriefView, number>;

export interface BuyerBriefJob {
    id: number;
    organizationId: number;
    briefId: number;
    supplierName?: string;
    jobType: string;
    status: BriefJobStatus;
    currentStep?: string;
    startTime: string;
    endTime?: string;
    failedReason?: any;
    retryCount: number;
}

export const EVALUATION_GOAL_LABELS: Record<EvaluationGoal, string> = {
    [EvaluationGoal.NEW_SUPPLIER_EVALUATION]: "New Supplier Evaluation",
    [EvaluationGoal.RENEWAL_DECISION]: "Renewal Decision",
    [EvaluationGoal.CONTRACT_NEGOTIATION_PREP]: "Contract Negotiation Prep",
    [EvaluationGoal.RISK_ASSESSMENT]: "Risk Assessment",
    [EvaluationGoal.COMPETITIVE_COMPARISON]: "Competitive Comparison",
    [EvaluationGoal.DUE_DILIGENCE]: "Due Diligence",
};

export const VERDICT_LABELS: Record<Verdict, string> = {
    [Verdict.STRONG_YES]: "Strong Yes",
    [Verdict.YES]: "Yes",
    [Verdict.CONDITIONAL]: "Conditional",
    [Verdict.RENEGOTIATE]: "Renegotiate",
    [Verdict.INSUFFICIENT_DATA]: "Inconclusive",
    [Verdict.NO]: "No",
    [Verdict.STRONG_NO]: "Strong No",
};

export const STATUS_LABELS: Record<BriefStatus, string> = {
    [BriefStatus.QUEUED]: "Queued",
    [BriefStatus.PROCESSING]: "Processing",
    [BriefStatus.COMPLETED]: "Completed",
    [BriefStatus.FAILED]: "Failed",
};

/**
 * Where a vendor's buyer brief lives: the newest completed V2 brief
 * ("briefs") or, failing that, a legacy brief held as a supplier record
 * ("supplier"). `type` is the SPA route segment the brief is shown under, so
 * the path is /<type>/<id> and nothing on the frontend has to know there are
 * two kinds.
 */
export interface BuyerBriefRef {
    type: "briefs" | "supplier";
    id: number;
}
